import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { FC, ReactNode } from 'react'
import { Title } from 'react-admin'
import { border, xxllncColor } from '../../layout/themes'

interface Props {
  title: string
  actions?: ReactNode
  controls?: ReactNode
  children: ReactNode
}

const PREFIX = 'xxllncDataPageLayout'

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  actions: `${PREFIX}-actions`,
  controls: `${PREFIX}-controls`,
  content: `${PREFIX}-content`,
}

const Root = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    boxSizing: 'border-box',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontSize: 22,
    fontWeight: 700,
    color: xxllncColor.dark100,
    letterSpacing: -0.3,
    lineHeight: 1.2,
    margin: 0,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  [`& .${classes.controls}`]: {
    flexShrink: 0,
    backgroundColor: '#fff',
    border: `${border.thin} ${xxllncColor.opDark10}`,
    borderRadius: border.radius.md,
    padding: theme.spacing(1.5, 2),
  },
  [`& .${classes.content}`]: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}))

/**
 * Full-viewport page layout for data-dense pages (lists, pivot tables).
 * Pins itself to ``100vh - 64px`` (AppBar height); content children are
 * responsible for their own ``overflow: auto`` so only that region scrolls.
 */
const DataPageLayout: FC<Props> = ({ title, actions, controls, children }) => (
  <Root className={classes.root}>
    <Title title={title} />
    <Box className={classes.header}>
      <Typography component="h1" className={classes.title}>
        {title}
      </Typography>
      {actions && <Box className={classes.actions}>{actions}</Box>}
    </Box>
    {controls && <Box className={classes.controls}>{controls}</Box>}
    <Box className={classes.content}>{children}</Box>
  </Root>
)

export default DataPageLayout
